import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import {
  aiSendFollowUpQuestionForDataExplorerStreamed,
  aiCloseSidebar,
  aiResetAll,
  aiSetResultsModalOpen,
  aiToggleSideBar,
} from "../../store/actions/dashboard/dashboard";
import { AI_RESTORE_CONVERSATION } from "../../store/actions/actionTypes";
import useApiQuery from "../../hooks/useApiQuery";
import { listAiPrompts } from "../../api/aiPrompts";
import AIResultsModal from "./AIResultsModal";

export function useAIResultsModal() {
  const dispatch = useDispatch();
  const aiSidebar = useSelector((state) => state.dashboard.aiSidebar);
  const history = useHistory();
  const controllerRef = useRef(new AbortController());
  const promptsQuery = useApiQuery(listAiPrompts, { enabled: false });

  useEffect(() => {
    return () => {
      controllerRef.current.abort();
    };
  }, []);

  const onSendFollowUpQuestion = async (question) => {
    controllerRef.current = new AbortController();
    const prompts = promptsQuery.data ?? (await promptsQuery.refetchData());
    const prompt = prompts.find((v) => v.key === "data-explorer");
    dispatch(
      aiSendFollowUpQuestionForDataExplorerStreamed(
        question,
        controllerRef.current.signal,
        { systemPromptOverride: prompt?.promptMessage }
      )
    );
  };

  const onStop = () => {
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
  };

  const askAi = (question) => {
    if (!question?.trim()) return;
    // If already on Data Explorer, use existing navigation flow
    if (history.location.pathname.includes("/data-explorer")) {
      history.push(
        `/admin/data-settings/data-explorer?q=${encodeURIComponent(
          question.trim()
        )}`
      );
      return;
    }
    dispatch(aiResetAll());
    dispatch(aiSetResultsModalOpen(true));
    onSendFollowUpQuestion(question);
  };

  const handleClose = () => {
    controllerRef.current.abort();
    dispatch(aiCloseSidebar());
  };

  const handleOpenInExplorer = () => {
    // Don't reset AI state — let the Data Explorer page pick it up
    dispatch(aiSetResultsModalOpen(false));
    dispatch(aiToggleSideBar(true));
  };

  const modalNode = aiSidebar.aiResultsModalOpen ? (
    <AIResultsModal
      onClose={handleClose}
      onSendFollowUpQuestion={onSendFollowUpQuestion}
      onStop={onStop}
      onOpenInExplorer={handleOpenInExplorer}
    />
  ) : null;

  const resumeConversation = async (conversation) => {
    const { getConversationMessages } = await import(
      "../../api/aiConversations"
    );
    const mapConversationMessages = (
      await import("../../utils/mapConversationMessages")
    ).default;

    const backendMessages = await getConversationMessages(conversation.uuid);
    const { messages, toolResponse } = mapConversationMessages(backendMessages);

    dispatch({
      type: AI_RESTORE_CONVERSATION,
      conversationId: conversation.uuid,
      title: conversation.title,
      messages,
      toolResponse,
    });
  };

  return { askAi, resumeConversation, modalNode };
}
